import type { Editor } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { NodeSelection } from "prosemirror-state";

/**
 * Per-user open/collapsed state for toggle blocks. Lives OUTSIDE the document
 * on purpose: flipping a chevron must not create transactions, block updates,
 * history entries, or collab churn — and must keep working in read-only editors.
 */

export const TOGGLE_DEFAULT_OPEN = false;

const storageKey = (editorKey: string) => `closot-toggle-open-v1-${editorKey}`;
const listenerKey = (editorKey: string, blockId: string) => `${editorKey}::${blockId}`;

class ToggleStateStore {
  private state = new Map<string, Map<string, boolean>>();
  private listeners = new Map<string, Set<() => void>>();
  private hydrated = new Set<string>();
  private persistTimers = new Map<string, ReturnType<typeof setTimeout>>();

  private hydrate(editorKey: string): Map<string, boolean> {
    let byBlock = this.state.get(editorKey);
    if (!byBlock) {
      byBlock = new Map();
      this.state.set(editorKey, byBlock);
    }
    if (!this.hydrated.has(editorKey)) {
      this.hydrated.add(editorKey);
      try {
        const raw = window.localStorage.getItem(storageKey(editorKey));
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object") {
            for (const [id, open] of Object.entries(parsed)) {
              if (typeof open === "boolean" && !byBlock.has(id)) byBlock.set(id, open);
            }
          }
        }
      } catch {
        // Quota/private-mode/SSR — memory-only is fine.
      }
    }
    return byBlock;
  }

  private schedulePersist(editorKey: string): void {
    const existing = this.persistTimers.get(editorKey);
    if (existing) clearTimeout(existing);
    this.persistTimers.set(
      editorKey,
      setTimeout(() => {
        this.persistTimers.delete(editorKey);
        try {
          const byBlock = this.state.get(editorKey);
          if (!byBlock) return;
          window.localStorage.setItem(storageKey(editorKey), JSON.stringify(Object.fromEntries(byBlock)));
        } catch {
          // Best-effort persistence only.
        }
      }, 150),
    );
  }

  /** Toggles without a blockId yet (transient) render open so they're never trapped shut. */
  isOpen(editorKey: string, blockId: string | null | undefined): boolean {
    if (!blockId) return true;
    return this.hydrate(editorKey).get(blockId) ?? TOGGLE_DEFAULT_OPEN;
  }

  setOpen(editorKey: string, blockId: string | null | undefined, open: boolean): void {
    if (!blockId) return;
    const byBlock = this.hydrate(editorKey);
    if (byBlock.get(blockId) === open) return;
    byBlock.set(blockId, open);
    this.notify(editorKey, blockId);
    this.schedulePersist(editorKey);
  }

  toggle(editorKey: string, blockId: string | null | undefined): void {
    if (!blockId) return;
    this.setOpen(editorKey, blockId, !this.isOpen(editorKey, blockId));
  }

  setMany(editorKey: string, blockIds: string[], open: boolean): void {
    for (const id of blockIds) this.setOpen(editorKey, id, open);
  }

  subscribe(editorKey: string, blockId: string, cb: () => void): () => void {
    const key = listenerKey(editorKey, blockId);
    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    set.add(cb);
    return () => {
      set.delete(cb);
      if (set.size === 0) this.listeners.delete(key);
    };
  }

  /** Synchronous on purpose: callers may flip state and measure layout in the same task. */
  private notify(editorKey: string, blockId: string): void {
    const set = this.listeners.get(listenerKey(editorKey, blockId));
    if (!set) return;
    for (const cb of Array.from(set)) cb();
  }
}

export const toggleStateStore = new ToggleStateStore();

/** Toggle state is keyed per note; editors without a tracker (HistoryEditor) share "anon". */
export function getToggleEditorKey(editor: Editor): string {
  const tracker = editor.extensionManager.extensions.find((ext) => ext.name === "blockIdAndChangeTracker");
  return (tracker?.options as { noteId?: string } | undefined)?.noteId || "anon";
}

export function isToggleNodeOpen(editor: Editor, node: PMNode): boolean {
  return toggleStateStore.isOpen(getToggleEditorKey(editor), node.attrs.blockId);
}

/** Flip the toggle containing the selection (or selected as a node). True if one was found. */
export function toggleOpenAtSelection(editor: Editor): boolean {
  const { selection } = editor.state;
  let toggleNode: PMNode | null = null;

  if (selection instanceof NodeSelection && selection.node.type.name === "toggle") {
    toggleNode = selection.node;
  } else {
    const $from = selection.$from;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === "toggle") {
        toggleNode = node;
        break;
      }
    }
  }

  if (!toggleNode?.attrs.blockId) return false;
  toggleStateStore.toggle(getToggleEditorKey(editor), toggleNode.attrs.blockId);
  return true;
}

/** Open every collapsed toggle ancestor of the block (and the block itself if a toggle). */
export function expandAncestorsOf(editor: Editor, blockId: string): void {
  const { doc } = editor.state;
  let targetPos: number | null = null;

  doc.descendants((node, pos) => {
    if (targetPos !== null) return false;
    if (node.attrs?.blockId === blockId) {
      targetPos = pos;
      return false;
    }
    return true;
  });

  if (targetPos === null) return;

  const editorKey = getToggleEditorKey(editor);
  const $pos = doc.resolve(targetPos);
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === "toggle") {
      toggleStateStore.setOpen(editorKey, node.attrs.blockId, true);
    }
  }
  const target = doc.nodeAt(targetPos);
  if (target?.type.name === "toggle") {
    toggleStateStore.setOpen(editorKey, target.attrs.blockId, true);
  }
}
